import { Button } from "antd";
import { FinanceEntryType } from "../typeDefs";
import { isInGstRegisteredPeriod } from "./taxConfig";

const escapeCsv = (value: unknown) => {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCsv = (filename: string, rows: Record<string, unknown>[]) => {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(escapeCsv).join(","),
    ...rows.map((row) => headers.map((header) => escapeCsv(row[header])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const gstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;
  const gst = (entry.amount * 3) / 23;
  if (entry.category === "Expenses" || entry.category === "Assets") {
    return gst * ((entry.apportionRate ?? 100) / 100);
  }
  return gst;
};

export const AccountantExportPack = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const entries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
    .sort((a, b) => a.date - b.date);

  const transactionRows = entries.map((item) => ({
    Date: new Date(item.date).toLocaleDateString("en-GB"),
    Vendor: item.vendor,
    Description: item.description,
    Category: item.category,
    SubCategory: item.subCategory,
    Amount: item.amount.toFixed(2),
    OriginalAmount: item.originalAmount?.toFixed(2) || item.amount.toFixed(2),
    BusinessUseRate: item.businessUseRate ?? 100,
    HomeOfficeExpense: item.isHomeOfficeExpense ? "Yes" : "No",
    PersonalCardSpend: item.isPersonalCardSpend ? "Yes" : "No",
    YearEndTreatment: item.yearEndTreatment || "",
    ReclassifiedFrom: item.reclassifiedFromCategory
      ? `${item.reclassifiedFromCategory} / ${item.reclassifiedFromSubCategory || ""}`
      : "",
    GSTPeriod: isInGstRegisteredPeriod(item.date) ? "Registered period" : "Pre-registration",
    GSTIncluded: item.gstIncluded ? "Yes" : "No",
    GSTRegistered: item.gstRegistered ? "Yes" : "No",
    GSTNumber: item.gstNumber || "",
    GSTAmount: gstAmount(item).toFixed(2),
    Receipt: item.receipt ? "Yes" : "No",
    BankTransactionId: item.bankTransactionId || "",
    Reconciliation: item.reconciliationConfidence || "",
  }));

  const gstRows = entries.map((item) => ({
    Date: new Date(item.date).toLocaleDateString("en-GB"),
    Vendor: item.vendor,
    Category: item.category,
    Amount: item.amount.toFixed(2),
    GSTAmount: gstAmount(item).toFixed(2),
    Direction: item.category === "Revenue" ? "Collected" : "Claimable",
  }));

  const assetRows = entries
    .filter((item) => item.category === "Assets")
    .map((item) => ({
      PurchaseDate: new Date(item.date).toLocaleDateString("en-GB"),
      Asset: item.description,
      Supplier: item.vendor,
      Cost: item.amount.toFixed(2),
      DepreciationMethod: item.depreciationMethod || "",
      DepreciationRate: item.depreciationRate || 0,
      Receipt: item.receipt ? "Yes" : "No",
    }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p>Export accountant-ready CSV files for the selected report date range.</p>
      <Button onClick={() => downloadCsv("sws-transactions.csv", transactionRows)}>
        Export transactions CSV
      </Button>
      <Button onClick={() => downloadCsv("sws-gst-summary.csv", gstRows)}>
        Export GST CSV
      </Button>
      <Button onClick={() => downloadCsv("sws-asset-register.csv", assetRows)}>
        Export asset register CSV
      </Button>
    </div>
  );
};
